import DashboardLayout from '@/components/DashboardLayout'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Download,
  ChevronLeft,
  ChevronRight,
  Users,
  Search,
  CheckCircle2,
  XCircle,
  RotateCcw,
} from 'lucide-react'
import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from 'recharts'

interface Teacher {
  id: number
  name: string
}

interface Row {
  id: string // pode ser composto: "1,2,3" (varios lesson ids do mesmo grupo)
  lesson_date: string
  start_time: string
  end_time: string
  label: string
  teacher: { id: number; name: string } | null
  capacity: number
  enrolled: number
  extras?: number
  percentage: number
  status: string
}

interface Summary {
  total_lessons: number
  booked_lessons: number
  total_capacity: number
  total_enrolled: number
  occupancy_pct: number
  booking_rate: number
  full_count: number
}

interface ByDay {
  date: string
  capacity: number
  enrolled: number
  lessons: number
  percentage: number
}

interface ByTeacher {
  teacher_id: number
  teacher_name: string
  capacity: number
  enrolled: number
  lessons: number
  percentage: number
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  start_date?: string
  end_date?: string
  teacher_id?: string
  status: string
  only_booked: boolean
  search?: string
}

interface Props {
  rows: Row[]
  summary: Summary
  by_day: ByDay[]
  by_teacher: ByTeacher[]
  pagination: Pagination
  filters: Filters
  teachers: Teacher[]
}

interface Participant {
  id: number
  name: string
  email: string
  phone: string
  is_main: boolean
  lesson_id: number
  attendance_status: string
}

const TEACHER_ALL = '__all__'

const barColor = (pct: number) => {
  if (pct >= 90) return 'bg-red-500'
  if (pct >= 60) return 'bg-amber-400'
  return 'bg-sky-500'
}

const textColor = (pct: number) => {
  if (pct >= 90) return 'text-red-600'
  if (pct >= 60) return 'text-amber-600'
  return 'text-sky-600'
}

const chartFill = (pct: number) => {
  if (pct >= 90) return '#ef4444'
  if (pct >= 60) return '#fbbf24'
  return '#0ea5e9'
}

export default function Occupancy({
  rows,
  summary,
  by_day,
  by_teacher,
  pagination,
  filters,
  teachers,
}: Props) {
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')
  const [teacherId, setTeacherId] = useState(filters.teacher_id || TEACHER_ALL)
  const [statusFilter, setStatusFilter] = useState(filters.status || 'all')
  const [onlyBooked, setOnlyBooked] = useState(filters.only_booked)
  const [search, setSearch] = useState(filters.search || '')

  const [participantsOpen, setParticipantsOpen] = useState(false)
  const [participantsTitle, setParticipantsTitle] = useState('')
  const [participants, setParticipants] = useState<Participant[]>([])
  const [loadingParticipants, setLoadingParticipants] = useState(false)

  const applyFilters = (page: number = 1) => {
    router.get(
      '/lessons/occupancy',
      {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        teacher_id: teacherId === TEACHER_ALL ? undefined : teacherId,
        status: statusFilter,
        only_booked: String(onlyBooked),
        search: search || undefined,
        page,
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  const openParticipants = async (row: Row) => {
    setParticipantsTitle(
      `${format(new Date(row.lesson_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })} · ${row.start_time}-${row.end_time}`,
    )
    setParticipantsOpen(true)
    setLoadingParticipants(true)
    setParticipants([])
    try {
      const res = await fetch(`/lessons/occupancy/${row.id}/participants`, {
        headers: { Accept: 'application/json' },
      })
      const data = await res.json()
      setParticipants(data.participants || [])
    } catch (e) {
      setParticipants([])
    } finally {
      setLoadingParticipants(false)
    }
  }

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (startDate) params.set('start_date', startDate)
    if (endDate) params.set('end_date', endDate)
    if (teacherId && teacherId !== TEACHER_ALL)
      params.set('teacher_id', teacherId)
    if (statusFilter) params.set('status', statusFilter)
    params.set('only_booked', String(onlyBooked))
    if (search) params.set('search', search)
    window.location.href = `/lessons/occupancy.csv?${params.toString()}`
  }

  const fmt = (d: Date) => format(d, 'yyyy-MM-dd')

  // Calcula start/end pra cada preset baseado em hoje.
  // Semana = segunda a domingo (padrao BR).
  const presetRange = (key: string): { start: string; end: string } | null => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const startOfWeek = (d: Date) => {
      const c = new Date(d)
      const day = c.getDay() // 0=dom, 1=seg
      const offset = day === 0 ? -6 : 1 - day
      c.setDate(c.getDate() + offset)
      return c
    }
    switch (key) {
      case 'today':
        return { start: fmt(today), end: fmt(today) }
      case 'yesterday': {
        const d = new Date(today)
        d.setDate(d.getDate() - 1)
        return { start: fmt(d), end: fmt(d) }
      }
      case 'tomorrow': {
        const d = new Date(today)
        d.setDate(d.getDate() + 1)
        return { start: fmt(d), end: fmt(d) }
      }
      case 'this_week': {
        const s = startOfWeek(today)
        const e = new Date(s)
        e.setDate(s.getDate() + 6)
        return { start: fmt(s), end: fmt(e) }
      }
      case 'last_week': {
        const s = startOfWeek(today)
        s.setDate(s.getDate() - 7)
        const e = new Date(s)
        e.setDate(s.getDate() + 6)
        return { start: fmt(s), end: fmt(e) }
      }
      case 'this_month': {
        const s = new Date(today.getFullYear(), today.getMonth(), 1)
        const e = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        return { start: fmt(s), end: fmt(e) }
      }
      case 'next_month': {
        const s = new Date(today.getFullYear(), today.getMonth() + 1, 1)
        const e = new Date(today.getFullYear(), today.getMonth() + 2, 0)
        return { start: fmt(s), end: fmt(e) }
      }
      default:
        return null
    }
  }

  const applyPreset = (key: string) => {
    if (key === 'custom') return
    const range = presetRange(key)
    if (!range) return
    setStartDate(range.start)
    setEndDate(range.end)
    router.get(
      '/lessons/occupancy',
      {
        start_date: range.start,
        end_date: range.end,
        teacher_id: teacherId === TEACHER_ALL ? undefined : teacherId,
        status: statusFilter,
        only_booked: String(onlyBooked),
        search: search || undefined,
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  // Detecta qual preset corresponde ao range atual (pra marcar selecionado)
  const detectActivePreset = (): string => {
    if (!startDate || !endDate) return 'custom'
    for (const k of [
      'today',
      'yesterday',
      'tomorrow',
      'this_week',
      'last_week',
      'this_month',
      'next_month',
    ]) {
      const r = presetRange(k)
      if (r && r.start === startDate && r.end === endDate) return k
    }
    return 'custom'
  }
  const activePreset = detectActivePreset()

  const dayChartData = by_day.map((d) => ({
    label: format(new Date(d.date + 'T00:00:00'), 'dd/MM', { locale: ptBR }),
    percentage: d.percentage,
    enrolled: d.enrolled,
    capacity: d.capacity,
  }))

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Taxa de Ocupação</h1>
            <p className="text-sm text-muted-foreground">
              Análise de quantas vagas das suas aulas estão sendo preenchidas no
              período
            </p>
          </div>
          <Button variant="outline" onClick={exportCsv}>
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        </div>

        {/* Stats principais */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-sky-500">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Ocupação geral
              </div>
              <div
                className={`text-3xl font-bold mt-1 ${textColor(summary.occupancy_pct)}`}
              >
                {summary.occupancy_pct}%
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.total_enrolled} vagas preenchidas de{' '}
                {summary.total_capacity}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Aulas com aluno
              </div>
              <div className="text-3xl font-bold mt-1">
                {summary.booked_lessons}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.booking_rate}% das {summary.total_lessons} listadas
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Aulas lotadas
              </div>
              <div className="text-3xl font-bold mt-1 text-red-600">
                {summary.full_count}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                já bateram a capacidade máxima
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Período
              </div>
              <div className="text-sm font-medium mt-1">
                {filters.start_date &&
                  format(
                    new Date(filters.start_date + 'T00:00:00'),
                    'dd/MM/yyyy',
                    { locale: ptBR },
                  )}
                {filters.start_date && filters.end_date && ' — '}
                {filters.end_date &&
                  format(
                    new Date(filters.end_date + 'T00:00:00'),
                    'dd/MM/yyyy',
                    { locale: ptBR },
                  )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {by_day.length} dia(s) com aulas
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="p-4">
            {/* Presets rapidos */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {[
                { key: 'today', label: 'Hoje' },
                { key: 'yesterday', label: 'Ontem' },
                { key: 'tomorrow', label: 'Amanhã' },
                { key: 'this_week', label: 'Semana atual' },
                { key: 'last_week', label: 'Semana passada' },
                { key: 'this_month', label: 'Mês atual' },
                { key: 'next_month', label: 'Próximo mês' },
              ].map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={activePreset === p.key ? 'default' : 'outline'}
                  onClick={() => applyPreset(p.key)}
                >
                  {p.label}
                </Button>
              ))}
              {activePreset === 'custom' && (
                <Button size="sm" variant="default" disabled>
                  Personalizado
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Aula ou professor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyFilters(1)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">De</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Até</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Professor</Label>
                <Select value={teacherId} onValueChange={setTeacherId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TEACHER_ALL}>Todos</SelectItem>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id.toString()}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Todas (exceto canceladas)
                    </SelectItem>
                    <SelectItem value="scheduled">Agendadas</SelectItem>
                    <SelectItem value="completed">Concluídas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  id="only-booked"
                  checked={onlyBooked}
                  onCheckedChange={setOnlyBooked}
                />
                <Label htmlFor="only-booked" className="text-sm cursor-pointer">
                  Mostrar apenas horários com aluno
                </Label>
              </div>
              <Button onClick={() => applyFilters(1)}>Aplicar filtros</Button>
            </div>
          </CardContent>
        </Card>

        {/* Gráfico por dia */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ocupação por dia</CardTitle>
          </CardHeader>
          <CardContent>
            {dayChartData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Sem dados para exibir.
              </div>
            ) : (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={dayChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" fontSize={12} />
                    <YAxis
                      fontSize={12}
                      tickFormatter={(v) => `${v}%`}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      formatter={(value, _name, item) => {
                        const p = (
                          item as {
                            payload?: { enrolled: number; capacity: number }
                          }
                        ).payload
                        return [
                          `${value}% (${p?.enrolled ?? 0}/${p?.capacity ?? 0})`,
                          'Ocupação',
                        ]
                      }}
                    />
                    <Bar dataKey="percentage" radius={[4, 4, 0, 0]}>
                      {dayChartData.map((d, idx) => (
                        <rect key={idx} fill={chartFill(d.percentage)} />
                      ))}
                      <LabelList
                        dataKey="percentage"
                        position="top"
                        fontSize={11}
                        formatter={(v) => `${v}%`}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Por professor */}
        {by_teacher.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Por professor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {by_teacher.map((t) => (
                  <div key={t.teacher_id} className="flex items-center gap-3">
                    <div className="w-40 text-sm font-medium truncate">
                      {t.teacher_name}
                    </div>
                    <div className="flex-1 relative h-6 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 ${barColor(t.percentage)} transition-all`}
                        style={{ width: `${Math.min(t.percentage, 100)}%` }}
                      />
                      <div className="relative h-full flex items-center justify-end pr-3 text-xs font-medium">
                        {t.enrolled}/{t.capacity}
                      </div>
                    </div>
                    <div
                      className={`text-sm font-semibold w-12 text-right ${textColor(t.percentage)}`}
                    >
                      {t.percentage}%
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela detalhada */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Detalhamento das aulas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Nenhuma aula encontrada com esses filtros.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aula</TableHead>
                    <TableHead>Horário</TableHead>
                    <TableHead>Professor</TableHead>
                    <TableHead className="w-[280px]">Ocupação</TableHead>
                    <TableHead className="text-right">Participantes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      <TableCell className="text-sm">
                        <div>
                          {format(
                            new Date(row.lesson_date + 'T00:00:00'),
                            'dd/MM/yyyy',
                            { locale: ptBR },
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.start_time} - {row.end_time}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.teacher?.name || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="relative w-full max-w-[180px] h-6 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`absolute inset-y-0 left-0 ${barColor(row.percentage)} transition-all`}
                              style={{
                                width: `${Math.min(row.percentage, 100)}%`,
                              }}
                            />
                            <div className="relative h-full flex items-center justify-center text-xs font-medium">
                              {row.enrolled}/{row.capacity}
                            </div>
                          </div>
                          <span
                            className={`text-sm font-semibold ${textColor(row.percentage)} min-w-[40px] text-right`}
                          >
                            {row.percentage}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openParticipants(row)}
                        >
                          <Users className="w-3 h-3 mr-1" /> Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-xs text-muted-foreground">
                  Página {pagination.current_page} de {pagination.total_pages} ·{' '}
                  {pagination.total_count} aulas
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.current_page === 1}
                    onClick={() => applyFilters(pagination.current_page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                    onClick={() => applyFilters(pagination.current_page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog participantes */}
      <Dialog open={participantsOpen} onOpenChange={setParticipantsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Participantes — {participantsTitle}</DialogTitle>
          </DialogHeader>
          {loadingParticipants ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Carregando...
            </div>
          ) : participants.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Nenhum aluno inscrito.
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {participants.map((p) => {
                const hasAttendance =
                  p.attendance_status === 'present' ||
                  p.attendance_status === 'absent'
                const setAttendance = (
                  status: 'present' | 'absent' | 'pending',
                ) => {
                  fetch(`/users/${p.id}/lessons/${p.lesson_id}/attendance`, {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                      'X-CSRF-Token':
                        (
                          document.querySelector(
                            'meta[name="csrf-token"]',
                          ) as HTMLMetaElement
                        )?.content || '',
                    },
                    body: JSON.stringify({ attendance_status: status }),
                  }).then(() => {
                    // Atualiza localmente sem fechar o modal
                    setParticipants((prev) =>
                      prev.map((x) =>
                        x.id === p.id && x.lesson_id === p.lesson_id
                          ? { ...x, attendance_status: status }
                          : x,
                      ),
                    )
                  })
                }
                return (
                  <div
                    key={`${p.id}-${p.lesson_id}`}
                    className="border-b pb-2 last:border-0"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">
                          {p.name}
                          {p.is_main && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              (principal)
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.email} · {p.phone}
                        </div>
                        <div className="mt-1.5 flex items-center gap-1">
                          {hasAttendance ? (
                            <>
                              <Badge
                                variant={
                                  p.attendance_status === 'present'
                                    ? 'default'
                                    : 'destructive'
                                }
                                className="text-[10px] h-5 px-1.5"
                              >
                                {p.attendance_status === 'present'
                                  ? 'Presente'
                                  : 'Faltou'}
                              </Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[10px]"
                                onClick={() => setAttendance('pending')}
                              >
                                <RotateCcw className="w-3 h-3 mr-0.5" />
                                Resetar
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-green-700 hover:bg-green-50"
                                onClick={() => setAttendance('present')}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Presente
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs text-red-700 hover:bg-red-50"
                                onClick={() => setAttendance('absent')}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Faltou
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => router.visit(`/users/${p.id}`)}
                      >
                        Ver
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
